import { CommunicationsIcon, PatientIcon, TaskIcon } from '@/components/ui/icons/TimelineIcons'
import type { TimelineEventCategory } from '@/types/timeline'
import BlockOutlined from '@mui/icons-material/BlockOutlined'
import EditOutlined from '@mui/icons-material/EditOutlined'
import LinkOutlined from '@mui/icons-material/LinkOutlined'
import PersonAddOutlined from '@mui/icons-material/PersonAddOutlined'
import LocalShippingOutlined from '@mui/icons-material/LocalShippingOutlined'
import MergeOutlined from '@mui/icons-material/MergeOutlined'
import PaidOutlined from '@mui/icons-material/PaidOutlined'
import PrintOutlined from '@mui/icons-material/PrintOutlined'
import ReceiptOutlined from '@mui/icons-material/ReceiptOutlined'
import ReplayOutlined from '@mui/icons-material/ReplayOutlined'
import ReplyOutlined from '@mui/icons-material/ReplyOutlined'
import SendOutlined from '@mui/icons-material/SendOutlined'
import SyncAltOutlined from '@mui/icons-material/SyncAltOutlined'
import VerifiedUserOutlined from '@mui/icons-material/VerifiedUserOutlined'
import type { SvgIconProps } from '@mui/material'

export interface NoteTypeConfig {
  title: string
  Icon: React.ComponentType<SvgIconProps>
  category: TimelineEventCategory
  iconBg?: string
  iconColor?: string
}

const NOTE_TYPE_CONFIGS: Record<string, NoteTypeConfig> = {
  fields_update: {
    title: 'Patient Updated',
    Icon: EditOutlined,
    category: 'Patient'
  },
  status_update: {
    title: 'Patient Status Changed',
    Icon: SyncAltOutlined,
    category: 'Patient'
  },
  email: {
    title: 'Email',
    Icon: CommunicationsIcon,
    category: 'Communications'
  },
  response: {
    title: 'Response Received',
    Icon: ReplyOutlined,
    category: 'Patient'
  },
  merge_task_created: {
    title: 'Merge Task Created',
    Icon: TaskIcon,
    category: 'Tasks'
  },
  link_task_created: {
    title: 'Link Task Created',
    Icon: TaskIcon,
    category: 'Tasks'
  },
  merge_task_deferred: {
    title: 'Merge Task Deferred',
    Icon: TaskIcon,
    category: 'Tasks'
  },
  link_task_deferred: {
    title: 'Link Task Deferred',
    Icon: TaskIcon,
    category: 'Tasks'
  },
  merge_task_not_duplicate: {
    title: 'Merge Task — Not Duplicate',
    Icon: TaskIcon,
    category: 'Tasks'
  },
  link_task_not_duplicate: {
    title: 'Link Task — Not Duplicate',
    Icon: TaskIcon,
    category: 'Tasks'
  },
  merge: {
    title: 'Patient Merged',
    Icon: MergeOutlined,
    category: 'Patient'
  },
  link: {
    title: 'Patient Linked',
    Icon: LinkOutlined,
    category: 'Patient'
  },

  // ID verification note_types
  id_verification: {
    title: 'ID Verification',
    Icon: VerifiedUserOutlined,
    category: 'Patient'
  },
  id_verification_reset: {
    title: 'ID Verification Reset',
    Icon: ReplayOutlined,
    category: 'Patient'
  },

  // CRM / API patient note_types
  create_patient_crm: {
    title: 'Patient Created from CRM',
    Icon: PersonAddOutlined,
    category: 'Patient'
  },
  update_patient_crm: {
    title: 'Patient Updated from CRM',
    Icon: EditOutlined,
    category: 'Patient'
  },
  create_patient: {
    title: 'Patient Created',
    Icon: PersonAddOutlined,
    category: 'Patient'
  },
  merge_task_automatic_created: {
    title: 'Automatic Merge Task Created',
    Icon: TaskIcon,
    category: 'Tasks'
  },

  // Order note_types
  created_shipment: {
    title: 'Shipment Created',
    Icon: LocalShippingOutlined,
    category: 'Order'
  },
  printed_shipping_label: {
    title: 'Shipping Label Printed',
    Icon: PrintOutlined,
    category: 'Order'
  },
  cancelled_shipment: {
    title: 'Shipment Cancelled',
    Icon: BlockOutlined,
    category: 'Order'
  },
  sent_to_titan: {
    title: 'Sent to Titan',
    Icon: SendOutlined,
    category: 'Order'
  },
  refund: {
    title: 'Refund Issued',
    Icon: PaidOutlined,
    category: 'Order'
  },

  // User note_types
  user_create: {
    title: 'User Created',
    Icon: PersonAddOutlined,
    category: 'Patient'
  },
  user_link: {
    title: 'Patient Linked to User',
    Icon: LinkOutlined,
    category: 'Patient'
  },

  // Legacy note_types
  update: {
    title: 'Patient Updated',
    Icon: EditOutlined,
    category: 'Patient'
  },
  delete: {
    title: 'Record Deleted',
    Icon: PatientIcon,
    category: 'Patient'
  },
  transaction: {
    title: 'Transaction',
    Icon: ReceiptOutlined,
    category: 'Order'
  },
  rest: {
    title: 'Manual Entry',
    Icon: PatientIcon,
    category: 'Patient'
  }
}

const FALLBACK_CONFIG: NoteTypeConfig = {
  title: 'Patient Entry',
  Icon: PatientIcon,
  category: 'Patient'
}

export function getNoteTypeConfig(noteType: string): NoteTypeConfig {
  return NOTE_TYPE_CONFIGS[noteType] ?? FALLBACK_CONFIG
}
